import { useState } from 'react'
import { X, Search, Film, Tv } from 'lucide-react'
import { useAuth } from '@clerk/clerk-react'
import { mutate } from 'swr'

interface TMDbResult {
  tmdbId: number
  name: string
  nameJa: string
  mediaType: 'movie' | 'tv'
  posterUrl?: string
  releaseDate?: string
  overview?: string
}

interface TitleSearchDialogProps {
  titleId: string
  currentName: string
  onClose: () => void
}

export function TitleSearchDialog({ titleId, currentName, onClose }: TitleSearchDialogProps) {
  const { getToken } = useAuth()
  const [searchQuery, setSearchQuery] = useState(currentName)
  const [results, setResults] = useState<TMDbResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isMatching, setIsMatching] = useState(false)

  const handleSearch = async () => {
    if (!searchQuery.trim()) return

    setIsSearching(true)
    try {
      const token = await getToken()
      const res = await fetch(`/api/v1/titles/${titleId}/search-tmdb`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query: searchQuery })
      })

      if (!res.ok) {
        throw new Error('Failed to search')
      }

      const data = await res.json()
      setResults(data.results || [])
    } catch (error) {
      console.error('Failed to search:', error)
      alert('検索に失敗しました')
    } finally {
      setIsSearching(false)
    }
  }

  const handleMatch = async (result: TMDbResult) => {
    setIsMatching(true)
    try {
      const token = await getToken()
      const res = await fetch(`/api/v1/titles/${titleId}/match`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tmdbId: result.tmdbId,
          mediaType: result.mediaType
        })
      })

      if (!res.ok) {
        throw new Error('Failed to match')
      }

      // Refresh the titles list
      mutate(['/api/v1/titles'])
      alert('マッチングしました')
      onClose()
    } catch (error) {
      console.error('Failed to match:', error)
      alert('マッチングに失敗しました')
    } finally {
      setIsMatching(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="relative bg-background rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-background border-b p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">TMDbから作品を検索</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4">
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="作品名を入力..."
              className="flex-1 px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              onClick={handleSearch}
              disabled={isSearching}
              className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50"
            >
              <Search className="h-5 w-5" />
            </button>
          </div>

          {results.length > 0 ? (
            <div className="space-y-3">
              {results.map((result) => (
                <div
                  key={`${result.mediaType}-${result.tmdbId}`}
                  className="flex gap-4 p-3 border rounded hover:bg-muted cursor-pointer"
                  onClick={() => handleMatch(result)}
                >
                  {result.posterUrl ? (
                    <img
                      src={result.posterUrl}
                      alt={result.name}
                      className="w-16 h-24 rounded object-cover"
                    />
                  ) : (
                    <div className="w-16 h-24 rounded bg-muted flex items-center justify-center">
                      {result.mediaType === 'movie' ? (
                        <Film className="h-8 w-8 text-muted-foreground" />
                      ) : (
                        <Tv className="h-8 w-8 text-muted-foreground" />
                      )}
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{result.name}</h3>
                      <span className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">
                        {result.mediaType === 'movie' ? '映画' : 'TV'}
                      </span>
                    </div>
                    {result.nameJa && (
                      <p className="text-sm text-muted-foreground mb-1">{result.nameJa}</p>
                    )}
                    {result.releaseDate && (
                      <p className="text-xs text-muted-foreground">{result.releaseDate}</p>
                    )}
                    {result.overview && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {result.overview}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              {isSearching ? '検索中...' : '作品名を入力して検索してください'}
            </div>
          )}
        </div>

        {isMatching && (
          <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p>マッチング中...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
